import React, { useState, useEffect, useRef } from 'react';
import { BASE_URL } from '../../config/axios';
import AlertConfirmModal from '../../components/common/AlertConfirmModal';
import { ExclamationCircleIcon } from '@heroicons/react/outline';

// بيانات افتراضية للاختبار في حالة عدم وجود بيانات حقيقية
const defaultQuizData = {
  questions: []
};

const Content = ({ 
  currentLesson,
  isQuizStarted,
  showResults,
  isLoading,
  showDetailedResults,
  handleQuizReset,
  setIsQuizStarted,
  handleShowResults,
  setShowDetailedResults,
  currentQuestionIndex,
  timeLeft,
  formatTime,
  quizData = defaultQuizData,
  currentAnswer,
  handleAnswerSelect,
  quizAnswers,
  handlePreviousQuestion,
  handleNextQuestion,
  calculateScore,
  isSidebarOpen
}) => {
  const [showConfirmEndQuiz, setShowConfirmEndQuiz] = useState(false);

  return (
    <div className={`flex-1 transition-all duration-300 ${isSidebarOpen ? 'mr-0 lg:mr-80' : 'mr-0'} bg-gray-50 dark:bg-gray-900 pt-16`}>
      {/* Content */}
      <div className="h-full w-full flex items-center justify-center px-4 lg:px-20">
        {/* Video Player - For embedded videos and lessons with embed code */}
        {((currentLesson.type === 'video' || currentLesson.type === 'video_embed' || 
          (currentLesson.type === 'lesson' && currentLesson.content_url && 
           (currentLesson.content_url.includes('youtube') || currentLesson.content_url.includes('iframe'))))) && (
          <div className="w-full max-w-4xl mx-auto px-4">
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {currentLesson.content_url ? (
                <div className="w-full h-full">
                  <iframe
                    className="w-full h-full"
                    src={currentLesson.content_url.includes('<iframe') ? 
                      (currentLesson.content_url.match(/src=['"]([^'"]+)['"]/)?.[1] || '') : 
                      (currentLesson.content_url.includes('youtube.com/watch?v=') ? 
                        currentLesson.content_url.replace('watch?v=', 'embed/') : 
                        currentLesson.content_url)}
                    title={currentLesson.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p>لا يتوفر فيديو لهذا الدرس حالياً</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* External Video URL (YouTube, etc) */}
        {currentLesson.type === 'video_url' && (
          <div className="w-full max-w-4xl mx-auto px-4">
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
              <iframe
                className="w-full h-full"
                src={currentLesson.content_url.replace('watch?v=', 'embed/')}
                title={currentLesson.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        {/* Uploaded Video Player - For .mp4 files */}
        {(currentLesson.type === 'video_file' || 
          (currentLesson.type === 'lesson' && currentLesson.content_url && 
           (currentLesson.content_url.endsWith('.mp4') || currentLesson.content_url.endsWith('.mov') || currentLesson.content_url.endsWith('.avi')))) && (
          <div className="w-full max-w-4xl mx-auto px-4">
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
              <video 
                className="w-full h-full" 
                controls 
                controlsList="nodownload"
                src={`${BASE_URL}${currentLesson.content_url}`}
              >
                متصفحك لا يدعم تشغيل الفيديو.
              </video>
            </div>
          </div>
        )}

        {/* Text Content or Lesson with text */}
        {(currentLesson.type === 'text' || (currentLesson.type === 'lesson' && currentLesson.content_text)) && (
          <div className="w-full max-w-4xl mx-auto px-4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="prose dark:prose-invert max-w-none">
              <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">
                {currentLesson.content_text}
              </p>
            </div>
          </div>
        )}

        {/* مودال تأكيد إنهاء الاختبار */}
        <AlertConfirmModal
          isOpen={showConfirmEndQuiz}
          onClose={() => setShowConfirmEndQuiz(false)}
          onConfirm={() => {
            setShowConfirmEndQuiz(false);
            handleShowResults();
          }}
          icon={ExclamationCircleIcon}
          iconColorClass="text-red-500"
          title="إنهاء الاختبار"
          message="هل أنت متأكد من إنهاء الاختبار ؟"
          confirmText="إنهاء"
          cancelText="إلغاء"
          confirmButtonClass="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
        />

        {/* Course Content */}
        {currentLesson.type === 'resource' && (
          // Current Lesson Data
          <div className="w-full max-w-lg px-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{currentLesson.title}.pdf</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">PDF • 2.5 MB</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  onClick={() => window.open(`${BASE_URL}/storage/${currentLesson.file_path}`, '_blank')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  عرض الملف
                </button>
                <button
                  onClick={() => {
                    const downloadUrl = `${BASE_URL}/storage/${currentLesson.file_path}`;
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', `${currentLesson.title}.pdf`);
                    link.setAttribute('target', '_blank');
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => {
                      document.body.removeChild(link);
                    }, 100);
                  }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  تحميل
                </button>
              </div>
            </div>
          </div>
        )}

        {currentLesson.type === 'group' && (
          <div className="w-full max-w-lg px-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                </svg>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{currentLesson.title}</h3>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  onClick={() => (window.location.href = currentLesson.link)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-6" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                  </svg>
                  انضم الآن
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Content */}
        {currentLesson.type === 'quiz' && (
          <div className="w-full max-w-3xl">
            {/* Quiz Start Screen */}
            {!isQuizStarted && !showResults && !isLoading && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                    {currentLesson.title}
                  </h2>
                  {/* معلومات الاختبار */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 flex items-center gap-3">
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">الوقت المتاح</div>
                        <div className="font-medium text-gray-900 dark:text-white">{currentLesson.duration || 20} دقيقة</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4 flex items-center gap-3">
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">عدد الأسئلة</div>
                        <div className="font-medium text-gray-900 dark:text-white">{currentLesson.questions_count || 0} أسئلة</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button 
                      onClick={() => {
                        if (quizData && quizData.questions && quizData.questions.length > 0) {
                          setIsQuizStarted(true);
                        } else {
                          alert('خطأ: لا توجد أسئلة متاحة للاختبار');
                        }
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      ابدأ الاختبار
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quiz Questions */}
            {isQuizStarted && !showResults && !isLoading && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col" style={{ maxHeight: '85vh' }}>
                {/* شريط الحالة - ثابت في الأعلى */}
                <div className="border-b border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 z-10">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    {/* رقم السؤال */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                          <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                        </div>
                        <div className="text-sm">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {currentQuestionIndex + 1}/{quizData.questions.length}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* عداد الوقت */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                          <svg className={`w-6 h-6 ${
                            timeLeft < 300
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-sm">
                          <div className={`text-2xl font-bold ${
                            timeLeft < 300
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}>
                            {formatTime(timeLeft)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* زر إنهاء الاختبار */}
                    <button
                      onClick={() => setShowConfirmEndQuiz(true)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
                      </svg>
                    </button>
                  </div>

                  {/* شريط التقدم */}
                  <div className="h-1 bg-gray-200 dark:bg-gray-700">
                    <div 
                      className="h-1 bg-blue-600 dark:bg-blue-500 transition-all duration-300"
                      style={{ width: `${((currentQuestionIndex + 1) / (quizData?.questions?.length || 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* محتوى السؤال مع إمكانية التمرير */}
                <div className="p-6 overflow-y-auto flex-1" style={{ maxHeight: 'calc(80vh - 160px)' }}>
                  <div className="mb-6">
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
                      {quizData?.questions?.[currentQuestionIndex]?.text || 'لا يوجد سؤال'}
                    </h3>
                    {/* صورة السؤال (بعد نص السؤال) */}
                    {(() => {
                      const q = quizData?.questions?.[currentQuestionIndex];
                      const img = q?.image;
                      if (!img) return null;
                      const isAbsolute = /^https?:\/\//i.test(img);
                      const src = isAbsolute ? img : `${BASE_URL}${img.startsWith('/') ? '' : '/'}${img}`;
                      return (
                        <div className="mt-2 mb-6 flex justify-center">
                          <img
                            src={src}
                            alt="صورة السؤال"
                            className="max-w-full max-h-80 object-contain rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 mx-auto"
                            loading="lazy"
                          />
                        </div>
                      );
                    })()}
                  </div>

                  {/* الإجابات */}
                  <div className="space-y-3 pb-4">
                    {(quizData?.questions?.[currentQuestionIndex]?.answers || []).map((answer) => (
                      <button
                        key={answer.id}
                        onClick={() => handleAnswerSelect(answer.id)}
                        className={`w-full text-right p-3 rounded-lg transition-colors border ${
                          currentAnswer === answer.id
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-400'
                            : 'bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-600 border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {answer.image && (
                            <div className="flex-shrink-0">
                              <img 
                                src={answer.image} 
                                alt="صورة الإجابة" 
                                className="w-20 h-20 rounded-lg object-cover"
                              />
                            </div>
                          )}
                          <span className="text-gray-900 dark:text-white">{answer.text}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                </div>

                {/* أزرار التنقل - ثابتة في الأسفل */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="flex justify-between">
                    <button
                      onClick={handlePreviousQuestion}
                      className={`px-4 py-2 rounded-lg transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white ${currentQuestionIndex === 0 ? 'invisible' : ''}`}
                    >
                      السابق
                    </button>
                    <button
                      onClick={currentQuestionIndex === quizData.questions.length - 1 ? handleShowResults : handleNextQuestion}
                      disabled={!currentAnswer}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        !currentAnswer
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          : currentQuestionIndex === quizData.questions.length - 1
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {currentQuestionIndex === quizData.questions.length - 1 ? 'إرسال' : 'التالي'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* شاشة التحميل */}
            {isLoading && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="p-8 text-center">
                  <div className="mb-6">
                    <div className="relative mx-auto mb-4" style={{ width: '120px', height: '120px' }}>
                      <svg className="transform -rotate-90 w-[120px] h-[120px]">
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          className="stroke-gray-200 dark:stroke-gray-700"
                          strokeWidth="8"
                          fill="none"
                        />
                        <circle
                          cx="60"
                          cy="60"
                          r="54"
                          className="stroke-blue-500 progress-circle"
                          strokeWidth="8"
                          fill="none"
                          style={{
                            strokeDasharray: '339.292',
                            strokeDashoffset: '339.292',
                            animation: 'progress 1.5s ease-in-out infinite'
                          }}
                        />
                      </svg>
                      {/* النص في النص */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          جاري
                        </div>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      حساب النتيجة...
                    </h3>
                  </div>
                </div>

                <style jsx>{`
                  @keyframes progress {
                    0% {
                      stroke-dashoffset: 339.292;
                    }
                    100% {
                      stroke-dashoffset: 0;
                    }
                  }
                `}</style>
              </div>
            )}

            {/* نتائج الاختبار */}
            {showResults && !showDetailedResults && !isLoading && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="p-8 text-center">
                  <div className="mb-6">
                    {/* حساب النتيجة */}
                    {(() => {
                      const score = calculateScore();
                      return score.percentage >= 70 ? (
                        <div className="inline-flex p-4 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
                          <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="inline-flex p-4 bg-red-100 dark:bg-red-900/30 rounded-xl mb-4">
                          <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
                          </svg>
                        </div>
                      );
                    })()}
                    
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {calculateScore().percentage >= 70 ? 'أحسنت!' : 'حاول مرة أخرى'}
                    </h3>
                    <div className="text-3xl font-bold mb-4">
                      <span className={calculateScore().percentage >= 70 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {calculateScore().correct}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">/{quizData.questions.length}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                      {calculateScore().percentage >= 70 
                        ? 'لقد اجتزت الاختبار بنجاح!' 
                        : 'لم تجتز الاختبار، راجع المادة وحاول مرة أخرى'}
                    </p>
                  </div>

                  <div className="flex flex-col md:flex-row justify-center gap-4">
                    <button
                      onClick={handleQuizReset}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      إعادة الاختبار
                    </button>
                    <button
                      onClick={() => setShowDetailedResults(true)}
                      className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg transition-colors"
                    >
                      عرض النتيجة التفصيلية
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* نتائج تفصيلية */}
            {showDetailedResults && !isLoading && (
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">نتيجة الاختبار التفصيلية</h3>
                    <div className="text-lg font-bold">
                      <span className={calculateScore().percentage >= 70 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {calculateScore().correct}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">/{quizData.questions.length}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2 scrollbar-hide">
                    {quizData.questions.map((question, index) => (
                      <div key={question.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              quizAnswers[index] === question.correctAnswer
                                ? 'bg-green-100 dark:bg-green-900/30'
                                : 'bg-red-100 dark:bg-red-900/30'
                            }`}>
                              {quizAnswers[index] === question.correctAnswer ? (
                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                              {question.text}
                            </h4>
                            
                            {/* عرض التنويه لو الإجابة غلط وفي تنويه - تحت السؤال مباشرة */}
                            {quizAnswers[index] !== question.correctAnswer && question.notification && (
                              <div className="mt-2 mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                  </svg>
                                  <p className="text-sm text-blue-700 dark:text-blue-300">{question.notification}</p>
                                </div>
                              </div>
                            )}

                            <div className="space-y-2">
                              {question.answers.map((answer) => (
                                <div
                                  key={answer.id}
                                  className={`p-3 rounded-lg ${
                                          answer.id === question.correctAnswer
                                            ? 'bg-green-100 dark:bg-green-900/30 border border-green-500'
                                            : answer.id === quizAnswers[index]
                                            ? 'bg-red-100 dark:bg-red-900/30 border border-red-500'
                                            : 'bg-gray-100 dark:bg-gray-700'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          {answer.id === question.correctAnswer && (
                                            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                          <span className={`${
                                            answer.id === question.correctAnswer
                                              ? 'text-green-900 dark:text-green-100'
                                              : answer.id === quizAnswers[index]
                                              ? 'text-red-900 dark:text-red-100'
                                              : 'text-gray-700 dark:text-gray-300'
                                          }`}>
                                            {answer.text}
                                            {answer.id === quizAnswers[index] && answer.id !== question.correctAnswer && (
                                              <span className="mr-2 font-bold text-red-600 dark:text-red-400">(إجابتك)</span>
                                            )}
                                            {answer.id === quizAnswers[index] && answer.id === question.correctAnswer && (
                                              <span className="mr-2 font-bold text-green-600 dark:text-green-400">(إجابتك)</span>
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-6 flex justify-center">
                          <button
                            onClick={handleQuizReset}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            إعادة الاختبار
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
      </div>
    </div>

  );
};

export default Content;
